import { workspaceApiFetch } from '@affine/core/modules/cloud';
import {
  createBlockIndexReviewLocator,
  createDatabaseFileCellIndexReviewLocator,
  type EmbeddingIndexReviewField,
  type EmbeddingIndexReviewLocator,
  type EmbeddingIndexReviewRegenerableField,
  type EmbeddingIndexReviewRegenerationResult,
  type EmbeddingIndexReviewSubjectSummary,
} from '@blocksuite/affine/shared/services';

import { parseDatabaseCellAttachmentSourceId } from '../database-cell-source-id';
import type { AttachmentViewerSource } from '../types';

type EmbeddingIndexReviewRegenerationResponse =
  | EmbeddingIndexReviewRegenerationResult
  | {
      queued: false;
      unavailable: {
        code: 'INDEX_REVIEW_REGENERATION_UNAVAILABLE';
        reason:
          | 'image-ai-unavailable'
          | 'ocr-ai-unavailable'
          | 'text-ai-unavailable';
        message: string;
      };
    };

export function createPdfBlockIndexReviewLocator(input: {
  workspaceId: string;
  docId: string;
  blockId: string;
  blobId: string | null | undefined;
  pageIndex: number;
}): EmbeddingIndexReviewLocator | null {
  return createBlockIndexReviewLocator({
    workspaceId: input.workspaceId,
    docId: input.docId,
    subjectKind: 'attachment-block',
    blockId: input.blockId,
    blobId: input.blobId,
    pageIndex: input.pageIndex,
  });
}

export function createPdfSourceIndexReviewLocator(input: {
  source: AttachmentViewerSource;
  workspaceId: string;
  pageIndex: number;
}): EmbeddingIndexReviewLocator | null {
  const { source } = input;
  if (source.kind === 'database-cell') {
    const parts = parseDatabaseCellAttachmentSourceId(source.id);
    if (!parts) return null;
    return createDatabaseFileCellIndexReviewLocator({
      workspaceId: input.workspaceId,
      docId: source.docId,
      databaseId: parts.databaseId,
      rowId: parts.rowId,
      propertyId: parts.propertyId,
      refId: source.databaseAttachmentRefId ?? parts.refId,
      blobId: parts.blobId,
      pageIndex: input.pageIndex,
    });
  }

  return createPdfBlockIndexReviewLocator({
    workspaceId: input.workspaceId,
    docId: source.docId,
    blockId: source.id,
    blobId: source.blobId,
    pageIndex: input.pageIndex,
  });
}

export function createPdfSearchCoverageAttachmentBlockId(
  source: AttachmentViewerSource
): string | null {
  if (source.kind !== 'database-cell') return source.id;
  const parts = parseDatabaseCellAttachmentSourceId(source.id);
  if (!parts) return null;
  return [
    'database-cell',
    parts.databaseId,
    parts.rowId,
    parts.propertyId,
    parts.refId,
    parts.blobId,
  ].join(':');
}

export async function fetchIndexReviewSubject(input: {
  serverBaseUrl: string;
  locator: EmbeddingIndexReviewLocator;
}): Promise<EmbeddingIndexReviewSubjectSummary> {
  return indexReviewFetch<EmbeddingIndexReviewSubjectSummary>(
    indexReviewSubjectUrl(input.serverBaseUrl, input.locator).toString(),
    { method: 'GET' },
    input.locator.workspaceId
  );
}

export async function saveIndexReviewField(input: {
  serverBaseUrl: string;
  locator: EmbeddingIndexReviewLocator;
  field: EmbeddingIndexReviewField;
  value: unknown;
  expectedActiveVersionId?: string | null;
}): Promise<void> {
  await indexReviewFetch<{ version: unknown }>(
    `${input.serverBaseUrl}/api/workspace/${input.locator.workspaceId}/index-review/subject`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        locator: input.locator,
        field: input.field,
        value: input.value,
        expectedActiveVersionId: input.expectedActiveVersionId ?? null,
      }),
    },
    input.locator.workspaceId
  );
}

export async function regenerateIndexReviewField(input: {
  serverBaseUrl: string;
  locator: EmbeddingIndexReviewLocator;
  field: EmbeddingIndexReviewRegenerableField;
  userHint: string;
  options?: {
    mediaType?: string | null;
    confirmed?: boolean;
    expectedActiveVersionId?: string | null;
  };
}): Promise<EmbeddingIndexReviewRegenerationResponse> {
  return indexReviewFetch<EmbeddingIndexReviewRegenerationResponse>(
    `${input.serverBaseUrl}/api/workspace/${input.locator.workspaceId}/index-review/subject/regenerate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        locator: input.locator,
        field: input.field,
        userHint: input.userHint,
        mediaType: input.options?.mediaType ?? null,
        confirmed: input.options?.confirmed === true,
        expectedActiveVersionId: input.options?.expectedActiveVersionId ?? null,
      }),
    },
    input.locator.workspaceId
  );
}

function indexReviewSubjectUrl(
  serverBaseUrl: string,
  locator: EmbeddingIndexReviewLocator
) {
  const url = new URL(
    `${serverBaseUrl}/api/workspace/${locator.workspaceId}/index-review/subject`
  );
  const params: Record<string, string | number | null | undefined> = {
    docId: locator.docId,
    subjectKind: locator.subjectKind,
    blockId: locator.blockId,
    databaseId: locator.databaseId,
    rowId: locator.rowId,
    propertyId: locator.propertyId,
    refId: locator.refId,
    blobId: locator.blobId,
    pageIndex: locator.pageIndex,
  };
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }
  return url;
}

async function indexReviewFetch<T>(
  path: string,
  init: RequestInit,
  workspaceId: string
): Promise<T> {
  const method = init.method ?? 'GET';
  const res = await workspaceApiFetch(
    path,
    { credentials: 'include', ...init },
    {
      workspaceId,
      category: method === 'GET' ? 'metadata' : 'foreground-action',
      mode: method === 'GET' ? 'background' : 'foreground',
      priority: method === 'GET' ? 'visible' : 'foreground',
    }
  );
  if (!res.ok) {
    throw new PdfIndexReviewHttpError(
      `Index review request failed: ${res.status}`,
      res.status
    );
  }
  return (await res.json()) as T;
}

export class PdfIndexReviewHttpError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

export function isPdfIndexReviewConflictError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    Number(error.status) === 409
  );
}
